<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import EmailTriageAPI from 'dashboard/api/emailTriage';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// RedHosting: e-mails do suporte@ que o filtro deixou fora da caixa
// (EmailTriage::ClassifyService). "E atendimento" traz o e-mail de volta.
const DATE_FORMAT = 'dd/MM/yyyy HH:mm';

const { t } = useI18n();
const entries = ref([]);
const isFetching = ref(true);
const promotingId = ref(null);

const fetchEntries = async () => {
  try {
    const { data } = await EmailTriageAPI.get();
    entries.value = data;
  } catch (error) {
    useAlert(t('EMAIL_TRIAGE.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

// O e-mail chega na caixa em alguns segundos: o servidor busca o original na Hostinger.
const promote = async entry => {
  promotingId.value = entry.id;
  try {
    await EmailTriageAPI.promote(entry.id);
    entries.value = entries.value.filter(item => item.id !== entry.id);
    useAlert(t('EMAIL_TRIAGE.PROMOTED'));
  } catch (error) {
    useAlert(t('EMAIL_TRIAGE.PROMOTE_ERROR'));
  } finally {
    promotingId.value = null;
  }
};

const receivedAt = entry =>
  messageTimestamp(new Date(entry.received_at).getTime() / 1000, DATE_FORMAT);

onMounted(fetchEntries);
</script>

<template>
  <div
    v-if="isFetching"
    class="flex items-center justify-center w-full h-full bg-n-surface-1"
  >
    <Spinner :size="24" />
  </div>
  <EmptyStateLayout
    v-else-if="!entries.length"
    :title="t('EMAIL_TRIAGE.EMPTY_TITLE')"
    :subtitle="t('EMAIL_TRIAGE.EMPTY_SUBTITLE')"
    :show-backdrop="false"
  />
  <section
    v-else
    class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1"
  >
    <header class="px-6 pt-6 pb-4 border-b shrink-0 border-n-weak">
      <h1 class="m-0 text-xl font-medium text-n-slate-12">
        {{ t('EMAIL_TRIAGE.HEADER') }}
      </h1>
      <p class="mt-1 mb-0 text-sm text-n-slate-11">
        {{ t('EMAIL_TRIAGE.SUBTITLE') }}
      </p>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div
        v-for="entry in entries"
        :key="entry.id"
        class="flex flex-wrap items-start gap-x-6 gap-y-2 py-4 border-b border-n-weak"
      >
        <div class="flex-1 min-w-[16rem]">
          <div class="flex items-baseline gap-2 min-w-0">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ entry.from_name || entry.from_email }}
            </span>
            <span
              v-if="entry.from_name"
              class="text-xs truncate text-n-slate-10"
            >
              {{ entry.from_email }}
            </span>
          </div>
          <div class="mt-0.5 text-sm truncate text-n-slate-12">
            {{ entry.subject || t('EMAIL_TRIAGE.NO_SUBJECT') }}
          </div>
          <p class="mt-0.5 mb-0 text-xs text-n-slate-11 line-clamp-2">
            {{ entry.snippet }}
          </p>
        </div>
        <div class="w-56 min-w-0 text-xs">
          <div class="text-n-slate-10 tabular-nums">
            {{ receivedAt(entry) }}
          </div>
          <div class="flex items-start gap-1 mt-1 text-n-slate-11">
            <Icon icon="i-lucide-filter" class="size-3.5 mt-px shrink-0" />
            <span>{{ entry.reason }}</span>
          </div>
        </div>
        <Button
          :label="t('EMAIL_TRIAGE.PROMOTE')"
          icon="i-lucide-inbox"
          slate
          faded
          xs
          class="ms-auto"
          :is-loading="promotingId === entry.id"
          @click="promote(entry)"
        />
      </div>
    </main>
  </section>
</template>
